import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Star } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface ServiceRatingProps {
  serviceId: string;
  bookingId?: string;
  serviceName: string;
  onSuccess?: () => void;
}

const ServiceRating = ({ serviceId, bookingId, serviceName, onSuccess }: ServiceRatingProps) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [hasRated, setHasRated] = useState(false);
  const [hoveredRating, setHoveredRating] = useState(0);

  useEffect(() => {
    if (bookingId) {
      checkIfUserRated();
    }
  }, [serviceId, bookingId]);

  const checkIfUserRated = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('service_ratings')
        .select('id')
        .eq('user_id', user.id)
        .eq('service_id', serviceId)
        .maybeSingle();

      setHasRated(!!data);
    } catch (error) {
      console.error('خطأ في فحص التقييم:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast({
        title: "تقييم مطلوب",
        description: "يرجى اختيار تقييم للخدمة",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "تسجيل الدخول مطلوب",
          description: "يجب تسجيل الدخول لتقييم الخدمة",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from('service_ratings')
        .insert({
          user_id: user.id,
          service_id: serviceId,
          booking_id: bookingId || null,
          rating: rating,
          comment: comment.trim() || null
        });

      if (error) {
        console.error('خطأ في إرسال التقييم:', error);
        
        let errorMessage = "حدث خطأ أثناء إرسال التقييم";
        if (error.code === '23505') {
          errorMessage = "لقد قمت بتقييم هذه الخدمة مسبقاً";
        }
        
        toast({
          title: "خطأ في التقييم",
          description: errorMessage,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "تم التقييم بنجاح",
        description: "شكراً لك على تقييم الخدمة",
      });

      setRating(0);
      setComment('');
      setHasRated(true);
      setIsDialogOpen(false);

      if (onSuccess) {
        onSuccess();
      }

    } catch (error) {
      console.error('خطأ غير متوقع في التقييم:', error);
      toast({
        title: "خطأ في التقييم",
        description: "حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          disabled={hasRated}
        >
          <Star className="h-4 w-4 mr-2" />
          {hasRated ? 'تم التقييم' : 'تقييم الخدمة'}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-right">تقييم الخدمة</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-4 text-right">
              كيف تقيم خدمة: {serviceName}؟
            </p>
            
            {/* نجوم التقييم */}
            <div className="flex justify-center gap-1 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className="focus:outline-none"
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  onClick={() => setRating(star)}
                >
                  <Star
                    className={`h-8 w-8 transition-colors ${
                      star <= (hoveredRating || rating)
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>
            
            {rating > 0 && (
              <p className="text-sm text-gray-600 mb-4">
                تقييمك: {rating} من 5 نجوم
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="comment" className="text-sm font-medium text-right block">
              تعليق (اختياري)
            </label>
            <Textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="اكتب تعليقك حول الخدمة..."
              className="text-right"
              rows={3}
              disabled={isSubmitting}
            />
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            disabled={isSubmitting || rating === 0}
          >
            {isSubmitting ? 'جاري الإرسال...' : 'إرسال التقييم'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ServiceRating;
